import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { ApplicationUIContext, Drawer, useDrawerState, Table } from '@yuhuinc/ui-library';
import { Button, Form, Space, Typography } from 'antd';
import { getTranslatedString } from 'helpers/i18n';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import SelectFeesTable from './Fees/SelectFeeTable';

const { Title } = Typography;

const StyledSpace = styled(Space)`
  width: 100%;

  .ant-picker {
    width: 100%;
  }
`;

const FeesTableRowActions = ({ feeTemplate, onUpdate, onDelete, selectedUnitId }) => {
  const [, editFeeDrawer] = useDrawerState(false);

  const editFeeText = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.fees.editFee'
  );

  return (
    <>
      <Space size="middle">
        <Button type="link" icon={<EditOutlined />} onClick={editFeeDrawer.open} />
        <Button type="link" icon={<DeleteOutlined />} onClick={onDelete} />
      </Space>

      <Drawer
        title={editFeeText}
        visible={editFeeDrawer.isVisible}
        closable
        destroyOnClose
        onClose={editFeeDrawer.close}
      >
        <SelectFeesTable
          selectedUnitId={selectedUnitId}
          editableFeeTemplate={feeTemplate}
          onAdd={onUpdate}
          closeDrawer={editFeeDrawer.close}
        />
      </Drawer>
    </>
  );
};

const FeesTable = ({ fees, selectedUnitId, operations }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const form = Form.useFormInstance();

  const feeText = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.fees.fee'
  );

  const triggerText = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.fees.trigger'
  );

  const columns = [
    {
      title: feeText,
      dataIndex: 'internal_name',
      key: 'internal_name',
      render: (_, record) => form.getFieldValue(['fee_templates', record.name, 'internal_name']),
    },
    {
      title: triggerText,
      dataIndex: 'trigger',
      key: 'trigger',
      render: (_, record) => {
        const feeEvents = form.getFieldValue(['fee_templates', record.name, 'fee_events']);
        return feeEvents.map((fee_event) => fee_event.trigger.label).join(', ');
      },
    },
    {
      key: 'actions',
      render: (_, record) => (
        <FeesTableRowActions
          selectedUnitId={selectedUnitId}
          feeTemplate={form.getFieldValue(['fee_templates', record.name])}
          onUpdate={(newValue) => {
            form.setFields([
              {
                name: ['fee_templates', record.name],
                newValue,
              },
            ]);
          }}
          onDelete={() => operations.remove(record.name)}
        />
      ),
    },
  ];

  return (
    <Table
      dataSource={fees}
      columns={columns}
      hideActionAndSearch
      size={isMobile ? 'middle' : 'small'}
      scroll={{ x: isMobile }}
    />
  );
};

const Fees = ({ selectedUnit }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [, addFeeDrawer] = useDrawerState(false);

  const feesText = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.fees'
  );

  const addFeeText = getTranslatedString(
    'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.fees.addFee'
  );

  return (
    <>
      <Form.List
        name="fee_templates"
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.fee_templates !== currentValues.fee_templates
        }
      >
        {(fields, operations) => {
          const onAddFeeTemplate = (feeTemplate) => operations.add(feeTemplate);

          return (
            <>
              <StyledSpace direction="vertical" size="middle">
                <Title level={isMobile ? 4 : 5} style={{ margin: 0 }}>
                  {feesText}
                </Title>

                {fields.length > 0 && (
                  <FeesTable
                    fees={fields}
                    selectedUnitId={selectedUnit.id}
                    operations={operations}
                  />
                )}

                <Button size={isMobile && 'large'} onClick={addFeeDrawer.open}>
                  {addFeeText}
                </Button>
              </StyledSpace>

              <Drawer
                title={addFeeText}
                visible={addFeeDrawer.isVisible}
                closable
                destroyOnClose
                onClose={addFeeDrawer.close}
              >
                <SelectFeesTable
                  selectedUnitId={selectedUnit.id}
                  onAdd={onAddFeeTemplate}
                  closeDrawer={addFeeDrawer.close}
                />
              </Drawer>
            </>
          );
        }}
      </Form.List>
    </>
  );
};

Fees.propTypes = {
  selectedUnit: PropTypes.object,
};

Fees.defaultProps = {
  selectedUnit: {},
};

export default Fees;
